"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  type ChecklistTask,
  type QuestionnaireAnswers,
  type TaskCategory,
  generateTaskList,
} from "@/lib/checklist-questionnaire";
import { TaskInfoModal } from "./TaskInfoModal";

interface ChecklistItem extends ChecklistTask {
  completed: boolean;
}

interface PersonalizedChecklistProps {
  userId: string | null;
  answers: QuestionnaireAnswers;
  onStartOver: () => void;
  onSaveChecklist?: () => void;
}

const STORAGE_KEY_PREFIX = "starlit-checklist-items-v2:";

function getStorageKey(userId: string | null): string {
  return `${STORAGE_KEY_PREFIX}${userId ?? "guest"}`;
}

const CATEGORY_INFO: Record<TaskCategory, { title: string; description: string }> = {
  immediate: {
    title: "As soon as possible",
    description: "Steps that usually happen in the first day or two.",
  },
  shortTerm: {
    title: "In the next few days",
    description: "Planning details for the service and gathering.",
  },
  longTerm: {
    title: "Later on",
    description: "Longer-term tasks around the estate, accounts, and remembrance.",
  },
};

export function PersonalizedChecklist({ userId, answers, onStartOver, onSaveChecklist }: PersonalizedChecklistProps) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [newTaskText, setNewTaskText] = useState("");
  const [newTaskCategory, setNewTaskCategory] = useState<TaskCategory>("immediate");
  const [selectedTask, setSelectedTask] = useState<ChecklistItem | null>(null);

  // Generate initial task list from answers
  const baseTasks = useMemo(() => generateTaskList(answers), [answers]);

  // Load completion state from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;

    const key = getStorageKey(userId);
    const stored = window.localStorage.getItem(key);

    if (stored) {
      try {
        const parsed = JSON.parse(stored) as { completedIds: string[]; customTasks: ChecklistItem[] };
        const completedSet = new Set(parsed.completedIds || []);
        const customTasks = parsed.customTasks || [];

        // Merge base tasks with completion state and custom tasks
        const mergedItems: ChecklistItem[] = [
          ...baseTasks.map((task) => ({
            ...task,
            completed: completedSet.has(task.id),
          })),
          ...customTasks,
        ];

        setItems(mergedItems);
        setLoaded(true);
        return;
      } catch {
        // Fall back to fresh list
      }
    }

    // Initialize with uncompleted base tasks
    setItems(baseTasks.map((task) => ({ ...task, completed: false })));
    setLoaded(true);
  }, [userId, baseTasks]);

  // Save to localStorage
  useEffect(() => {
    if (typeof window === "undefined" || !loaded) return;

    const key = getStorageKey(userId);
    const baseTaskIds = new Set(baseTasks.map((t) => t.id));
    const completedIds = items.filter((i) => i.completed).map((i) => i.id);
    const customTasks = items.filter((i) => !baseTaskIds.has(i.id));

    window.localStorage.setItem(key, JSON.stringify({ completedIds, customTasks }));
  }, [items, loaded, userId, baseTasks]);

  const grouped = useMemo(
    () => ({
      immediate: items.filter((item) => item.category === "immediate"),
      shortTerm: items.filter((item) => item.category === "shortTerm"),
      longTerm: items.filter((item) => item.category === "longTerm"),
    }),
    [items],
  );

  const progress = useMemo(() => {
    const total = items.length;
    const completed = items.filter((i) => i.completed).length;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  }, [items]);

  const toggleItem = useCallback((id: string) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, completed: !item.completed } : item)));
  }, []);

  const deleteItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const showTaskInfo = useCallback((item: ChecklistItem) => {
    setSelectedTask(item);
  }, []);

  const handleAddTask = useCallback(() => {
    const text = newTaskText.trim();
    if (!text) return;

    const newItem: ChecklistItem = {
      id: `custom-${Date.now().toString(36)}`,
      text,
      category: newTaskCategory,
      completed: false,
    };

    setItems((prev) => [...prev, newItem]);
    setNewTaskText("");
  }, [newTaskText, newTaskCategory]);

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Header */}
      <header className="space-y-4">
        <div className="space-y-2 text-center sm:text-left">
          <p className="pill inline-flex text-[0.7rem] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Your Personalized Checklist
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            A guided path for your situation
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">
            Based on your answers, we've created a personalized checklist to help guide you through this process. You
            can add your own tasks or remove any that don't apply.
          </p>
        </div>

        {/* Progress bar */}
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">Overall progress</span>
            <span className="text-muted-foreground">
              {items.filter((i) => i.completed).length} of {items.length} tasks
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>{items.length} tasks personalized for your situation</p>
          <div className="flex items-center gap-2">
            {!userId && onSaveChecklist && (
              <button type="button" onClick={onSaveChecklist} className="btn btn-primary px-3 py-1.5 text-xs">
                Save checklist
              </button>
            )}
            <button type="button" onClick={onStartOver} className="btn btn-ghost px-3 py-1.5 text-xs">
              Start over with new answers
            </button>
          </div>
        </div>
      </header>

      {/* Main card */}
      <section className="card-elevated space-y-8 p-6 sm:p-8">
        {/* Sections */}
        {(["immediate", "shortTerm", "longTerm"] as TaskCategory[]).map((category) => {
          const categoryItems = grouped[category];
          if (categoryItems.length === 0) return null;

          const info = CATEGORY_INFO[category];
          return (
            <ChecklistSection
              key={category}
              title={info.title}
              description={info.description}
              items={categoryItems}
              onToggle={toggleItem}
              onDelete={deleteItem}
              onInfo={showTaskInfo}
            />
          );
        })}

        {/* Add custom task */}
        <div className="border-t border-border pt-6">
          <h2 className="text-sm font-semibold tracking-tight text-foreground">Add your own task</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Everyone's situation is different. Add anything you'd like to remember here.
          </p>
          <form
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
            onSubmit={(e) => {
              e.preventDefault();
              handleAddTask();
            }}
          >
            <label className="flex-1 text-xs text-muted-foreground">
              <span className="mb-1 block text-[0.7rem] font-medium uppercase tracking-[0.16em]">Task</span>
              <input
                type="text"
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                className="w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                placeholder="For example: Call insurance company"
              />
            </label>

            <label className="text-xs text-muted-foreground sm:w-40">
              <span className="mb-1 block text-[0.7rem] font-medium uppercase tracking-[0.16em]">When</span>
              <select
                value={newTaskCategory}
                onChange={(e) => setNewTaskCategory(e.target.value as TaskCategory)}
                className="w-full rounded-2xl border border-border bg-card px-3 py-2 text-sm text-foreground outline-none ring-0 transition focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="immediate">As soon as possible</option>
                <option value="shortTerm">Next few days</option>
                <option value="longTerm">Later on</option>
              </select>
            </label>

            <button type="submit" className="btn btn-primary mt-1 w-full sm:mt-5 sm:w-auto">
              Add task
            </button>
          </form>
        </div>
      </section>

      {/* Task info modal */}
      <TaskInfoModal
        taskId={selectedTask?.id ?? null}
        taskText={selectedTask?.text ?? ""}
        onClose={() => setSelectedTask(null)}
      />
    </div>
  );
}

interface ChecklistSectionProps {
  title: string;
  description: string;
  items: ChecklistItem[];
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  onInfo: (item: ChecklistItem) => void;
}

function ChecklistSection({ title, description, items, onToggle, onDelete, onInfo }: ChecklistSectionProps) {
  const completedCount = items.filter((i) => i.completed).length;

  return (
    <section className="space-y-3">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight text-foreground sm:text-lg">{title}</h2>
          <span className="text-xs text-muted-foreground">
            {completedCount}/{items.length} done
          </span>
        </div>
        <p className="text-xs text-muted-foreground sm:text-sm">{description}</p>
      </header>

      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.id}>
            <div className="flex items-start gap-3 rounded-2xl border border-border bg-card px-3 py-2.5 text-sm shadow-sm">
              <button
                type="button"
                onClick={() => onToggle(item.id)}
                className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-border bg-background align-middle outline-none ring-0 transition hover:border-primary focus-visible:ring-2 focus-visible:ring-primary/40"
                aria-pressed={item.completed}
                aria-label={item.completed ? "Mark as not done" : "Mark as done"}
              >
                {item.completed && <span className="block h-2.5 w-2.5 rounded-full bg-primary" />}
              </button>

              <div className="flex-1 space-y-1">
                <p
                  className={`text-[0.9rem] leading-relaxed ${
                    item.completed ? "text-muted-foreground line-through" : "text-foreground"
                  }`}
                >
                  {item.text}
                </p>
              </div>

              <div className="flex flex-shrink-0 items-center gap-1">
                {/* Info button - only for non-custom tasks */}
                {!item.id.startsWith("custom-") && (
                  <button
                    type="button"
                    onClick={() => onInfo(item)}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-secondary hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40"
                    aria-label="Learn more about this task"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onDelete(item.id)}
                  className="btn btn-ghost px-2 py-1 text-[0.7rem]"
                >
                  Remove
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
